'use strict';
/* ==========================================================================
   core-local/paper_figs.js — the Pamphlet's figures (content/paper.json sections[].figure, PAPER.md), drawn with plain
   2D-canvas calls on a context that is PASSED IN: the stage's `g` (landscape: the figure panel beside the text) or an
   inline <canvas> in the paper (portrait dock: the figure stacked above its section's text). No engine sprite cache,
   no shadowBlur: a figure is a pure function of (figure data, Ratify, highlight), so the same code draws on both.

   Every vote count, margin, order, amendment count and date in the ledger, strip and funnel comes from
   core-local/ratify.js (window.Ratify: STATES, bor, article7, recommendedStates); the rows in paper.json are there for
   review only (tools/contentcheck.js proves they equal the model). Flow / compare / quotes print their JSON labels.

   Layout is in EMs of the base font size `fs` the caller picks (a readable size where the figure lands): every figure
   is about 24 em wide; PaperFigs.height(fig, w, fs) gives the height an inline canvas needs.

   PaperFigs.draw(c, fig, { x, y, w, h, fs, hi })   draw `fig` into the box (x, y, w, h) of context c
   PaperFigs.height(fig, w, fs)                      preferred height for width w (inline figures)
   PaperFigs.match(fig, text, prev)                  the figure part a sentence talks about → hi (or null: keep `prev`)
                                                     flow {box}, compare {rows}, quotes {item}, ledger {codes, count},
                                                     strip {codes, bracket}, funnel {levels, slips, tally}
   PaperFigs.labels(fig)                             every text the figure prints (contentcheck: glyphs, US spelling)
   ========================================================================== */
(function (root) {
  const C = { navy: '#1f2f55', brick: '#9e2b25', cream: '#fbf5e6', paper: '#f3ead7', parch: '#efe2c2', gold: '#b08a2e',
    ink: '#2b2420', wax: '#8e1f1b', hi: 'rgba(242,179,61,0.55)', faint: 'rgba(43,36,32,0.35)', rule: 'rgba(43,36,32,0.22)' };
  const SLAB = "'Alfa Slab One', Georgia, serif", TYPE = "'Special Elite', 'Courier New', monospace", HAND = "'Caveat Brush', 'Comic Sans MS', cursive";
  const R = () => root.Ratify || (typeof require === 'function' ? require('./ratify.js') : null);

  /* ---------------- tiny drawing helpers (context passed in) ---------------- */
  function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; }; }
  function font(c, size, face) { c.font = `${Math.round(size * 10) / 10}px ${face}`; }
  function text(c, s, x, y, size, face, color, align = 'left', base = 'alphabetic') {
    font(c, size, face); c.fillStyle = color; c.textAlign = align; c.textBaseline = base; c.fillText(s, x, y);
  }
  /* shrink to fit width */
  function fit(c, s, x, y, size, face, color, maxW, align = 'left', base = 'alphabetic') {
    font(c, size, face); const w = c.measureText(s).width;
    text(c, s, x, y, w > maxW ? size * maxW / w : size, face, color, align, base);
  }
  function wrap(c, s, maxW, size, face) {
    font(c, size, face);
    const out = []; let line = '';
    for (const word of String(s).split(/\s+/)) {
      const t = line ? line + ' ' + word : word;
      if (line && c.measureText(t).width > maxW) { out.push(line); line = word; } else line = t;
    }
    if (line) out.push(line);
    return out;
  }
  function lines(c, arr, x, y, size, face, color, lh, align = 'left') {
    arr.forEach((l, i) => text(c, l, x, y + i * lh, size, face, color, align, 'top'));
    return arr.length * lh;
  }
  /* a paper sheet: a flat offset shadow, the fill, a thin ink edge; torn = a jagged top and bottom */
  function sheet(c, x, y, w, h, { fill = C.cream, edge = 'rgba(43,36,32,0.45)', seed = 1, torn = false, lw = 1.5, rot = 0, shadow = true } = {}) {
    c.save();
    if (rot) { c.translate(x + w / 2, y + h / 2); c.rotate(rot); c.translate(-(x + w / 2), -(y + h / 2)); }
    const path = () => {
      c.beginPath();
      if (!torn) { c.rect(x, y, w, h); return; }
      const r = rng(seed), n = Math.max(6, Math.round(w / 14));
      c.moveTo(x, y + r() * 4);
      for (let i = 1; i <= n; i++) c.lineTo(x + w * i / n, y + r() * 6);
      c.lineTo(x + w, y + h - r() * 4);
      for (let i = n - 1; i >= 0; i--) c.lineTo(x + w * i / n, y + h - r() * 6);
      c.closePath();
    };
    if (shadow) { c.save(); c.translate(lw * 2, lw * 2.5); path(); c.fillStyle = 'rgba(52,34,18,0.18)'; c.fill(); c.restore(); }
    path(); c.fillStyle = fill; c.fill();
    c.lineWidth = lw; c.strokeStyle = edge; c.stroke();
    c.restore();
  }
  /* an ink stroke with a little hand jitter (seeded: the same every frame) */
  function inkLine(c, pts, { w = 2, color = C.ink, seed = 3, amp = 0.8 } = {}) {
    const r = rng(seed);
    c.save(); c.lineWidth = w; c.strokeStyle = color; c.lineCap = 'round'; c.lineJoin = 'round';
    c.beginPath();
    pts.forEach(([px, py], i) => { const jx = (r() - 0.5) * amp, jy = (r() - 0.5) * amp; if (i) c.lineTo(px + jx, py + jy); else c.moveTo(px + jx, py + jy); });
    c.stroke(); c.restore();
  }
  function arrow(c, x1, y1, x2, y2, { w = 2.5, color = C.ink, head = 10, seed = 5 } = {}) {
    inkLine(c, [[x1, y1], [(x1 + x2) / 2 + 1, (y1 + y2) / 2], [x2, y2]], { w, color, seed });
    const a = Math.atan2(y2 - y1, x2 - x1);
    c.save(); c.fillStyle = color; c.beginPath();
    c.moveTo(x2, y2); c.lineTo(x2 - head * Math.cos(a - 0.45), y2 - head * Math.sin(a - 0.45));
    c.lineTo(x2 - head * Math.cos(a + 0.45), y2 - head * Math.sin(a + 0.45)); c.closePath(); c.fill(); c.restore();
  }
  function glow(c, x, y, w, h) { c.save(); c.fillStyle = C.hi; c.fillRect(x, y, w, h); c.restore(); }
  function seal(c, x, y, r, label, { fill = C.wax, ink = C.cream, fs = 12, seed = 7 } = {}) {
    const rn = rng(seed);
    c.save(); c.beginPath();
    for (let i = 0; i <= 18; i++) { const a = i / 18 * Math.PI * 2, rr = r * (0.93 + rn() * 0.12); c[i ? 'lineTo' : 'moveTo'](x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    c.closePath(); c.fillStyle = 'rgba(52,34,18,0.2)'; c.translate(1.5, 2); c.fill(); c.translate(-1.5, -2);
    c.fillStyle = fill; c.fill();
    c.beginPath(); c.arc(x, y, r * 0.72, 0, Math.PI * 2); c.lineWidth = Math.max(1, r * 0.06); c.strokeStyle = 'rgba(255,240,220,0.35)'; c.stroke();
    c.restore();
    text(c, label, x, y + fs * 0.04, fs, SLAB, ink, 'center', 'middle');
  }
  function title(c, fig, x, y, w, fs) {
    fit(c, fig.title || '', x + w / 2, y, fs * 1.05, SLAB, C.ink, w - fs, 'center', 'top');
    return fs * 1.75;
  }

  /* ---------------- sentence → figure part ---------------- */
  const NUMW = { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', ten: '10', eleven: '11',
    twelve: '12', thirteen: '13', fourteen: '14', seventeen: '17' };
  const STOP = new Set(['the', 'of', 'a', 'an', 'and', 'to', 'in', 'it', 'its', 'by', 'for', 'is', 'no.']);
  const norm = w => { const t = String(w).toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '').replace(/['’]s$/, ''); return NUMW[t] || t; };
  const stem = t => (t.length >= 5 ? t.slice(0, 5) : t);
  const toks = s => String(s).split(/\s+/).map(norm).filter(Boolean);
  const stems = s => new Set(toks(s).map(stem));
  const has = (S, words) => words.filter(w => S.has(w)).length;

  function match(fig, sentence, prev) {
    if (!fig || !sentence) return null;
    const S = stems(sentence), Rt = R();
    switch (fig.kind) {
      case 'flow': {
        const sets = (fig.boxes || []).map(b => [...stems(b.label)].filter(t => !STOP.has(t)));
        const own = sets.map((s, i) => s.filter(t => sets.every((o, j) => j === i || !o.includes(t))));
        let best = -1, bn = 0;
        own.forEach((s, i) => { const n = has(S, s); if (n > bn) { bn = n; best = i; } });
        return best >= 0 ? { box: best } : null;
      }
      case 'compare': {
        const rows = new Set();
        (fig.rows || []).forEach((r, i) => { const ws = [...stems(r.issue)].filter(t => !STOP.has(t)); if (has(S, ws)) rows.add(i); });
        return rows.size ? { rows } : null;
      }
      case 'quotes': {
        const low = String(sentence).toLowerCase();
        let item = -1;
        (fig.items || []).forEach((it, i) => {
          const q = String(it.quote || '').toLowerCase().slice(0, 24);
          const ws = [...stems(it.who)].filter(t => !STOP.has(t) && !/^\d/.test(t));
          if ((q && low.includes(q)) || has(S, ws)) item = i;
        });
        return item >= 0 ? { item } : null;
      }
      case 'ledger': case 'strip': {
        if (!Rt) return null;
        const codes = new Set(Rt.STATES.filter(s => sentence.includes(s.name)).map(s => s.code));
        const bracket = fig.kind === 'strip' && /after it|ratified after/i.test(sentence);
        if (!codes.size && !bracket) return null;
        const prevCount = prev && prev.count || 0;
        const count = Math.max(prevCount, ...[...codes].map(k => Rt.order(k)));
        return { codes, count, bracket };
      }
      case 'funnel': {
        if (!Rt) return null;
        const b = Rt.bor, T = new Set(toks(sentence)), lv = new Set();
        [b.house, b.sent, b.ratified].forEach((n, i) => { if (T.has(String(n))) lv.add(i); });
        if (/Bill of Rights/.test(sentence) && !lv.size) lv.add(2);
        const slips = T.has(String(b.art27)) || T.has(String(b.art27num) + 'th') || /leftover/i.test(sentence);
        const tally = T.has(String(b.states)) || T.has(String(b.needed)) || /three-fourths/i.test(sentence);
        return lv.size || slips || tally ? { levels: lv, slips, tally } : null;
      }
    }
    return null;
  }

  /* ---------------- the figures ---------------- */
  const H_EM = { flow: 20.2, compare: 19, quotes: 14.5, ledger: 20.4, strip: 14.2, funnel: 16.5 };
  function height(fig, w, fs) { return Math.round((H_EM[fig && fig.kind] || 14) * fs); }

  function flow(c, fig, x, y, w, h, fs, hi) {
    let yy = y + title(c, fig, x, y, w, fs);
    const boxes = fig.boxes || [], arrows = fig.arrows || [];
    const nb = boxes.length, notesN = (boxes[0] && boxes[0].notes || []).length;
    const big = fs * (1.6 + Math.ceil(notesN / 2) * 1.12 + 0.3), small = fs * 2.75, gap = fs * 1.75;
    const scale = Math.min(1, (h - (yy - y) - fs * 0.4) / (big + (nb - 1) * (small + gap)));
    const bw = w - fs * 2.2, bx = x + fs * 1.1;
    boxes.forEach((b, i) => {
      const bh = (i === 0 ? big : small) * scale, on = hi && hi.box === i;
      if (on) glow(c, bx - fs * 0.35, yy - fs * 0.3, bw + fs * 0.7, bh + fs * 0.6);
      sheet(c, bx, yy, bw, bh, { fill: i === 0 ? C.parch : C.cream, torn: i === 0, seed: 11 + i, edge: on ? C.ink : 'rgba(43,36,32,0.45)', lw: on ? 2.5 : 1.5 });
      const ls = fs * 1.02 * Math.min(1, scale * 1.1);
      fit(c, b.label, bx + fs * 0.6, yy + fs * 0.35, ls, SLAB, i === 0 ? C.brick : C.navy, bw * (i === 0 ? 0.95 : 0.58), 'left', 'top');
      const notes = b.notes || [];
      if (i === 0) {
        const cw = (bw - fs * 1.2) / 2;
        notes.forEach((n, k) => {
          const nx = bx + fs * 0.6 + (k % 2) * cw, ny = yy + fs * 1.65 * scale + Math.floor(k / 2) * fs * 1.12 * scale;
          fit(c, n, nx, ny, fs * 0.78, TYPE, C.ink, cw - fs * 0.4, 'left', 'top');
          font(c, fs * 0.78, TYPE); const tw = Math.min(c.measureText(n).width, cw - fs * 0.4);
          inkLine(c, [[nx - 2, ny + fs * 0.45], [nx + tw + 2, ny + fs * 0.4]], { w: Math.max(1.2, fs * 0.08), color: C.brick, seed: 40 + k });
        });
      } else {
        // the date on the right of the sheet, a little mark for each kind
        fit(c, notes.join(' · '), bx + bw - fs * 0.6, yy + bh / 2, fs * 0.95, TYPE, C.ink, bw * 0.38, 'right', 'middle');
      }
      if (i < nb - 1) {
        const ay0 = yy + bh + fs * 0.12, ay1 = yy + bh + gap * scale - fs * 0.12, ax = bx + fs * 2.2;
        arrow(c, ax, ay0, ax, ay1, { w: Math.max(1.5, fs * 0.12), head: fs * 0.45, seed: 60 + i });
        if (arrows[i]) fit(c, arrows[i], ax + fs * 0.8, (ay0 + ay1) / 2, fs * 0.92, HAND, C.navy, bw - fs * 3.6, 'left', 'middle');
      }
      yy += bh + (i < nb - 1 ? gap * scale : 0);
    });
  }

  function compare(c, fig, x, y, w, h, fs, hi) {
    let yy = y + title(c, fig, x, y, w, fs);
    const cols = fig.columns || ['Federalists', 'Anti-Federalists'];
    const gw = fs * 5.4, cw = (w - gw - fs * 0.6) / 2, lx = x + fs * 0.3, gx = lx + cw, rx = gx + gw;
    sheet(c, x, yy, w, y + h - yy - fs * 0.2, { fill: C.cream, seed: 21 });
    // headers: a woodtype letter on its block + the side's name
    const head = (cx, letter, name, col) => {
      c.fillStyle = col; c.fillRect(cx + fs * 0.4, yy + fs * 0.4, fs * 1.5, fs * 1.6);
      text(c, letter, cx + fs * 1.15, yy + fs * 1.25, fs * 1.2, SLAB, C.cream, 'center', 'middle');
      fit(c, name.toUpperCase(), cx + fs * 2.2, yy + fs * 1.25, fs * 0.85, SLAB, col, cw - fs * 2.5, 'left', 'middle');
    };
    head(lx, 'F', cols[0], C.navy); head(rx, 'A', cols[1], C.brick);
    yy += fs * 2.4;
    const rows = fig.rows || [], fsz = fs * 0.8, lh = fs * 0.98;
    const wrapped = rows.map(r => ({ l: wrap(c, r.left, cw - fs * 0.9, fsz, HAND), r: wrap(c, r.right, cw - fs * 0.9, fsz, HAND), i: wrap(c, r.issue, gw - fs * 0.4, fs * 0.72, TYPE) }));
    const need = wrapped.reduce((a, q) => a + Math.max(q.l.length, q.r.length, q.i.length) * lh + fs * 0.7, 0);
    const k = Math.min(1, (y + h - yy - fs * 0.5) / need);
    wrapped.forEach((q, i) => {
      const rh = (Math.max(q.l.length, q.r.length, q.i.length) * lh + fs * 0.7) * k, on = hi && hi.rows && hi.rows.has(i);
      if (on) glow(c, x + fs * 0.15, yy - fs * 0.1, w - fs * 0.3, rh);
      inkLine(c, [[x + fs * 0.4, yy - fs * 0.15], [x + w - fs * 0.4, yy - fs * 0.15]], { w: 1, color: C.rule, seed: 80 + i, amp: 0.5 });
      const ty = yy + fs * 0.25 * k;
      q.l.forEach((s, j) => text(c, s, lx + fs * 0.45, ty + j * lh * k, fsz * Math.min(1, k * 1.1), HAND, C.navy, 'left', 'top'));
      q.r.forEach((s, j) => text(c, s, rx + fs * 0.45, ty + j * lh * k, fsz * Math.min(1, k * 1.1), HAND, C.brick, 'left', 'top'));
      q.i.forEach((s, j) => text(c, s, gx + gw / 2, ty + j * lh * k, fs * 0.72, TYPE, C.ink, 'center', 'top'));
      if (on) inkLine(c, [[gx + fs * 0.3, yy + rh - fs * 0.32], [gx + gw - fs * 0.3, yy + rh - fs * 0.34]], { w: Math.max(1.5, fs * 0.1), color: C.ink, seed: 90 + i });
      yy += rh;
    });
    inkLine(c, [[gx, y + fs * 4.1], [gx, yy - fs * 0.2]], { w: 1, color: C.rule, seed: 97 });
    inkLine(c, [[rx, y + fs * 4.1], [rx, yy - fs * 0.2]], { w: 1, color: C.rule, seed: 98 });
  }

  function quotes(c, fig, x, y, w, h, fs, hi) {
    let yy = y + title(c, fig, x, y, w, fs);
    const items = fig.items || [];
    const lab = fig.centerLabel || '';
    fit(c, lab, x + w / 2, yy + fs * 0.1, fs * 1.05, HAND, C.ink, w * 0.8, 'center', 'top');
    yy += fs * 1.7;
    const bw = (w - fs * 1.6) / 2, bh = y + h - yy - fs * 0.6;
    items.forEach((it, i) => {
      const bx = x + fs * 0.5 + i * (bw + fs * 0.6), on = hi && hi.item === i, col = it.side === 'anti' ? C.brick : C.navy;
      const rot = (i ? 1 : -1) * 0.035;
      c.save(); c.translate(bx + bw / 2, yy + bh / 2); c.rotate(rot); c.translate(-(bx + bw / 2), -(yy + bh / 2));
      if (on) glow(c, bx - fs * 0.35, yy - fs * 0.35, bw + fs * 0.7, bh + fs * 0.7);
      sheet(c, bx, yy, bw, bh, { fill: C.cream, seed: 31 + i, edge: col, lw: on ? 3.5 : 2.5 });
      c.fillStyle = col; c.fillRect(bx, yy, bw, fs * 0.45);
      // the quote, as large as fits the broadside
      let qs = fs * 1.15, ql;
      for (; qs > fs * 0.6; qs *= 0.92) { ql = wrap(c, '“' + it.quote + '”', bw - fs * 1.1, qs, SLAB); if (ql.length * qs * 1.18 <= bh - fs * 3.2) break; }
      lines(c, ql, bx + bw / 2, yy + fs * 1.0, qs, SLAB, C.ink, qs * 1.18, 'center');
      inkLine(c, [[bx + bw * 0.2, yy + bh - fs * 1.9], [bx + bw * 0.8, yy + bh - fs * 1.9]], { w: 1.2, color: C.faint, seed: 35 + i });
      const wl = wrap(c, it.who, bw - fs * 0.8, fs * 0.72, TYPE);
      lines(c, wl.slice(0, 2), bx + bw / 2, yy + bh - fs * 1.6, fs * 0.72, TYPE, col, fs * 0.82, 'center');
      c.restore();
    });
  }

  function ledger(c, fig, x, y, w, h, fs, hi, Rt) {
    let yy = y + title(c, fig, x, y, w, fs);
    const S = Rt.STATES, nine = Rt.article7, full = !hi || hi.count == null;
    const count = full ? S.length : hi.count;
    sheet(c, x, yy, w, y + h - yy - fs * 0.1, { fill: C.cream, seed: 41 });
    // the counter
    const ratified = Math.min(count, S.length);
    text(c, ratified < nine ? `RATIFIED: ${ratified} of ${nine} needed` : `RATIFIED: ${ratified}  ·  ${nine} needed`, x + w / 2, yy + fs * 0.45, fs * 0.95, SLAB, ratified >= nine ? C.navy : C.brick, 'center', 'top');
    yy += fs * 1.75;
    const nineGap = fs * 0.75;
    const rh = Math.min(fs * 1.22, (y + h - yy - fs * 0.5 - nineGap) / S.length), rf = Math.min(fs * 0.78, rh * 0.68);
    const oX = x + fs * 0.4, nX = oX + fs * 1.3, wX = nX + fs * 6.4, bX = wX + fs * 3.7, mX = x + w - fs * 0.4, bW = mX - fs * 4.1 - bX;
    S.forEach((s, i) => {
      const ry = yy + i * rh + (s.order > nine ? nineGap : 0), cy = ry + rh / 2, on = hi && hi.codes && hi.codes.has(s.code), done = s.order <= count;
      if (on) glow(c, x + fs * 0.1, ry, w - fs * 0.2, rh);
      c.save(); c.globalAlpha = done ? 1 : 0.32;
      text(c, String(s.order), oX + fs * 0.9, cy, rf, TYPE, C.ink, 'right', 'middle');
      fit(c, s.name, nX, cy, rf, TYPE, C.ink, fs * 6.2, 'left', 'middle');
      text(c, s.shown, wX, cy, rf * 0.92, TYPE, 'rgba(43,36,32,0.75)', 'left', 'middle');
      const tot = s.yes + s.no, yw = bW * s.yes / tot, bh = rh * 0.52;
      c.fillStyle = C.navy; c.fillRect(bX, cy - bh / 2, yw, bh);
      c.fillStyle = C.brick; c.fillRect(bX + yw, cy - bh / 2, bW - yw, bh);
      text(c, `${s.yes}–${s.no}`, bX + fs * 0.25, cy + rf * 0.03, rf * 0.82, TYPE, C.cream, 'left', 'middle');
      const m = Rt.margin(s.code), un = s.no === 0;
      const close = !un && Rt.flip(s.code) <= 2;
      if (un) text(c, 'unanimous', mX, cy, rf * 1.1, HAND, C.gold, 'right', 'middle');
      else text(c, close ? `margin ${m}` : `+${m}`, mX, cy, rf * (close ? 1.1 : 0.95), close ? HAND : TYPE, close ? C.brick : C.ink, 'right', 'middle');
      c.restore();
      if (s.order === nine) {
        // the line across the sheet under the ninth state, its label in the gap
        const ly = ry + rh + nineGap / 2;
        font(c, nineGap * 0.85, SLAB); const lw = c.measureText('NINE').width;
        inkLine(c, [[x + fs * 0.3, ly], [x + w / 2 - lw / 2 - fs * 0.3, ly]], { w: Math.max(1.5, fs * 0.1), color: C.navy, seed: 47 });
        inkLine(c, [[x + w / 2 + lw / 2 + fs * 0.3, ly], [x + w - fs * 0.3, ly]], { w: Math.max(1.5, fs * 0.1), color: C.navy, seed: 48 });
        text(c, 'NINE', x + w / 2, ly + nineGap * 0.05, nineGap * 0.85, SLAB, C.navy, 'center', 'middle');
      }
      const row = (fig.rows || []).find(r => r.state === s.name);
      if (row && row.note) { font(c, rf, TYPE); const nx0 = nX + c.measureText(s.name).width + fs * 0.25; fit(c, row.note, nx0, cy, rf * 0.6, TYPE, C.brick, Math.max(fs, wX - nx0 - fs * 0.2), 'left', 'middle'); }
    });
  }

  function strip(c, fig, x, y, w, h, fs, hi, Rt) {
    let yy = y + title(c, fig, x, y, w, fs);
    const S = Rt.STATES, rec = new Set(Rt.recommendedStates());
    const first = S.find(s => rec.has(s.code));
    const after = S.filter(s => first && s.order > first.order), afterRec = after.filter(s => rec.has(s.code)).length;
    const perRow = Math.ceil(S.length / 2), sp = Math.min((w - fs * 0.6) / perRow, fs * 3.6), r = Math.min(sp * 0.4, fs * 1.35);
    const rowH = (y + h - yy - fs * 1.9) / 2;
    const pos = s => { const i = s.order - 1, row = i < perRow ? 0 : 1, k = row ? i - perRow : i, n = row ? S.length - perRow : perRow;
      return { x: x + w / 2 + (k - (n - 1) / 2) * sp, y: yy + row * rowH + r + fs * 0.4, row }; };
    // the bracket over the states after the first: one rule per row, the count once
    if (after.length) {
      const on = hi && hi.bracket;
      [0, 1].forEach(row => {
        const ps = after.map(pos).filter(p => p.row === row);
        if (!ps.length) return;
        const x0 = Math.min(...ps.map(p => p.x)) - r * 1.1, x1 = Math.max(...ps.map(p => p.x)) + r * 1.1, by = ps[0].y - r - fs * 0.25;
        if (on) glow(c, x0 - fs * 0.2, by - fs * 0.25, x1 - x0 + fs * 0.4, r * 2 + fs * 2.6);
        inkLine(c, [[x0, by + fs * 0.3], [x0, by], [x1, by], [x1, by + fs * 0.3]], { w: Math.max(1.4, fs * 0.09), color: C.brick, seed: 51 + row });
      });
      const p0 = pos(after[0]);
      text(c, `${afterRec} of the ${after.length} after it`, Math.min(x + w - fs * 0.4, p0.x + r * 1.2 + fs * 0.4), p0.y - r - fs * 0.35, fs * 0.92, HAND, C.brick, 'left', 'bottom');
    }
    S.forEach(s => {
      const p = pos(s), on = hi && hi.codes && hi.codes.has(s.code);
      if (on) glow(c, p.x - r * 1.25, p.y - r * 1.25, r * 2.5, r * 2.5 + fs * 1.6);
      seal(c, p.x, p.y, r, s.code, { fs: r * 0.62, seed: 70 + s.order, fill: rec.has(s.code) ? C.wax : '#7d6a55' });
      if (rec.has(s.code)) {
        // a quill mark and a rolled list hang under the seal
        const qy = p.y + r + fs * 0.2;
        inkLine(c, [[p.x - r * 0.5, qy + fs * 1.0], [p.x + r * 0.45, qy]], { w: Math.max(1.4, fs * 0.09), color: C.navy, seed: 90 + s.order });
        c.fillStyle = C.parch; c.strokeStyle = C.ink; c.lineWidth = 1;
        c.fillRect(p.x - r * 0.2, qy + fs * 0.35, r * 0.75, fs * 0.6); c.strokeRect(p.x - r * 0.2, qy + fs * 0.35, r * 0.75, fs * 0.6);
      }
      const row = (fig.states || []).find(q => q.state === s.name);
      if (row && row.note) {
        const nl = wrap(c, row.note, sp * 1.1, fs * 0.6, TYPE);
        lines(c, nl, p.x, p.y + r + fs * 0.25, fs * 0.6, TYPE, C.ink, fs * 0.66, 'center');
      }
      if (first && s.code === first.code) {
        const lab = (row && row.label) || 'the first';
        text(c, lab, p.x, p.y - r - fs * 0.3, fs * 0.92, HAND, C.navy, 'center', 'bottom');
      }
    });
  }

  function funnel(c, fig, x, y, w, h, fs, hi, Rt) {
    let yy = y + title(c, fig, x, y, w, fs);
    const b = Rt.bor, L = fig.levels || [], ns = [b.house, b.sent, b.ratified];
    const gap = fs * 1.05, cx = x + w * 0.42, maxW = w * 0.8;
    let sh = fs * 2.3;
    L.slice(0, 3).forEach((lv, i) => {
      const lw = maxW * (1 - i * 0.24), lx = cx - lw / 2, on = hi && hi.levels && hi.levels.has(i);
      font(c, fs * 1.45, SLAB); const nw0 = c.measureText(String(ns[i])).width;
      sh = Math.max(fs * 2.3, wrap(c, lv.label, lw - nw0 - fs * 1.3, fs * 0.74, TYPE).length * fs * 0.84 + fs * 0.7);
      if (on) glow(c, lx - fs * 0.3, yy - fs * 0.25, lw + fs * 0.6, sh + fs * 0.5);
      sheet(c, lx, yy, lw, sh, { fill: i === 2 ? C.parch : C.cream, seed: 101 + i, edge: on ? C.ink : 'rgba(43,36,32,0.45)', lw: on ? 2.5 : 1.5 });
      text(c, String(ns[i]), lx + fs * 0.5, yy + sh / 2, fs * 1.45, SLAB, i === 2 ? C.brick : C.navy, 'left', 'middle');
      font(c, fs * 1.45, SLAB); const nw = c.measureText(String(ns[i])).width;
      const ll = wrap(c, lv.label, lw - nw - fs * 1.3, fs * 0.74, TYPE);
      lines(c, ll, lx + nw + fs * 0.85, yy + sh / 2 - (ll.length * fs * 0.84) / 2, fs * 0.74, TYPE, C.ink, fs * 0.84);
      if (i < 2) {
        const nx = cx - maxW * (1 - (i + 1) * 0.24) / 2;
        inkLine(c, [[lx + fs * 0.4, yy + sh], [nx + fs * 0.4, yy + sh + gap]], { w: 1.4, color: C.faint, seed: 110 + i });
        inkLine(c, [[lx + lw - fs * 0.4, yy + sh], [cx + (cx - nx) - fs * 0.4, yy + sh + gap]], { w: 1.4, color: C.faint, seed: 112 + i });
      }
      yy += sh + gap;
    });
    // two of the twelve fall out sideways: the 27th Amendment (1992) and the one never ratified
    const slips = fig.sideSlips || [], on = hi && hi.slips;
    const labels = [`${b.art27num}th Amendment, ${b.art27}`, (slips[1] && slips[1].label) || 'never ratified'];
    const sx = cx + maxW * 0.38 + fs * 0.2, sy0 = y + fs * 1.75 + sh + gap * 0.3, sw = Math.min(fs * 8.2, x + w - sx - fs * 0.4);
    labels.forEach((lab, i) => {
      const yy2 = sy0 + i * fs * 1.9;
      if (on) glow(c, sx - fs * 0.2, yy2 - fs * 0.2, sw + fs * 0.4, fs * 1.6);
      sheet(c, sx, yy2, sw, fs * 1.25, { fill: C.cream, seed: 120 + i, rot: (i ? -1 : 1) * 0.06 });
      fit(c, lab, sx + sw / 2, yy2 + fs * 0.63, fs * 0.72, TYPE, i ? C.brick : C.navy, sw - fs * 0.5, 'center', 'middle');
    });
    arrow(c, cx + maxW * (1 - 0.24) / 2 - fs * 0.3, y + fs * 1.75 + sh * 1.5 + gap, sx - fs * 0.2, sy0 + fs * 0.6, { w: 1.4, color: C.faint, head: fs * 0.35, seed: 125 });
    // the tally: 14 state marks, 11 inked in
    const tOn = hi && hi.tally, tx = x + fs * 0.6, ty = yy + fs * 0.1, mk = Math.min(fs * 0.62, (w - fs * 1.2) / (b.states * 1.25));
    if (tOn) glow(c, tx - fs * 0.25, ty - fs * 0.2, w - fs * 0.7, fs * 2.6);
    for (let i = 0; i < b.states; i++) {
      const px = tx + i * mk * 1.25;
      c.save(); c.lineWidth = 1.2; c.strokeStyle = C.ink; c.fillStyle = i < b.needed ? C.navy : 'transparent';
      c.fillRect(px, ty, mk, mk); c.strokeRect(px, ty, mk, mk); c.restore();
    }
    text(c, `${b.needed} of ${b.states} needed (3/4 of ${b.states} = ${b.threeQuarters})`, tx, ty + mk + fs * 0.3, fs * 0.8, HAND, C.navy, 'left', 'top');
  }

  function draw(c, fig, { x = 0, y = 0, w, h, fs = 16, hi = null } = {}) {
    if (!fig || !c) return;
    const Rt = R();
    c.save();
    try {
      switch (fig.kind) {
        case 'flow': flow(c, fig, x, y, w, h, fs, hi); break;
        case 'compare': compare(c, fig, x, y, w, h, fs, hi); break;
        case 'quotes': quotes(c, fig, x, y, w, h, fs, hi); break;
        case 'ledger': if (Rt) ledger(c, fig, x, y, w, h, fs, hi, Rt); break;
        case 'strip': if (Rt) strip(c, fig, x, y, w, h, fs, hi, Rt); break;
        case 'funnel': if (Rt) funnel(c, fig, x, y, w, h, fs, hi, Rt); break;
        default: text(c, fig.title || '', x + w / 2, y + h / 2, fs, SLAB, C.ink, 'center', 'middle');
      }
    } finally { c.restore(); }
  }

  /* every string a figure prints (tools/contentcheck.js: glyphs + spelling) */
  function labels(fig) {
    const Rt = R(), out = [fig.title || ''];
    if (fig.kind === 'flow') (fig.boxes || []).forEach(b => out.push(b.label, ...(b.notes || []))), out.push(...(fig.arrows || []));
    if (fig.kind === 'compare') { out.push(...(fig.columns || [])); (fig.rows || []).forEach(r => out.push(r.issue, r.left, r.right)); }
    if (fig.kind === 'quotes') { out.push(fig.centerLabel || ''); (fig.items || []).forEach(i => out.push('“' + i.quote + '”', i.who)); }
    if (fig.kind === 'ledger' && Rt) { out.push('RATIFIED: 0 of 9 needed', 'unanimous', 'margin 3', 'NINE'); Rt.STATES.forEach(s => out.push(s.name, s.shown, `${s.yes}–${s.no}`)); (fig.rows || []).forEach(r => r.note && out.push(r.note)); }
    if (fig.kind === 'strip' && Rt) { out.push('6 of the 7 after it', 'the first'); (fig.states || []).forEach(r => { if (r.note) out.push(r.note); if (r.label) out.push(r.label); }); }
    if (fig.kind === 'funnel' && Rt) { (fig.levels || []).forEach(l => out.push(l.label)); out.push('27th Amendment, 1992', '11 of 14 needed (3/4 of 14 = 10.5)'); (fig.sideSlips || []).forEach(s => out.push(s.label)); }
    return out.filter(Boolean);
  }

  const PaperFigs = { draw, height, match, labels, C };
  root.PaperFigs = PaperFigs;
  if (typeof module !== 'undefined' && module.exports) module.exports = PaperFigs;
})(typeof window !== 'undefined' ? window : globalThis);
